import React, { useState } from "react";
import { MessageCircleHeart, ChevronDown } from "lucide-react";
import { Field } from "./scShared";

export const DIRE_QUESTIONS = [
  { id: "trop", label: (other) => `Je devine ce que ${other} a trop entendu cette semaine` },
  { id: "pas", label: (other) => `Je devine ce que ${other} n'a pas assez entendu cette semaine` },
  { id: "exag", label: () => "Ce qui a été exagéré chez moi" },
  { id: "pasExag", label: (other) => `Ce qui n'a pas été exagéré chez ${other}` },
];

const Column = ({ who, other, color, bg, values, onChange, testid }) => (
  <div className="rounded-3xl p-4 sm:p-5 space-y-3" style={{ background: bg }} data-testid={testid}>
    <h4 className="font-serif-title text-xl" style={{ color }}>{who}</h4>
    {DIRE_QUESTIONS.map((q) => (
      <Field key={q.id} label={q.label(other)} value={values[q.id] || ""} onChange={(v) => onChange(q.id, v)} placeholder="…" testid={`${testid}-${q.id}`} multiline />
    ))}
  </div>
);

export default function ScDire({ week, setWeek, childName, parentName }) {
  const dire = week.dire || { on: false, enfant: {}, parent: {} };
  const [reveal, setReveal] = useState(false);
  const set = (patch) => setWeek({ dire: { ...dire, ...patch } });
  const setAns = (who, id, v) => set({ [who]: { ...(dire[who] || {}), [id]: v } });
  const child = childName || "l'enfant";
  const parent = parentName || "le parent";

  return (
    <section className="rounded-3xl bg-white/80 border border-[var(--border)] overflow-hidden shadow-sm" data-testid="sc-dire">
      <button onClick={() => set({ on: !dire.on })} className="w-full flex items-center gap-4 p-5 sm:p-7 text-left" data-testid="sc-dire-toggle">
        <div className="w-12 h-12 rounded-2xl bg-[#FDE4DF] text-[#B4483A] flex items-center justify-center shrink-0"><MessageCircleHeart size={24} /></div>
        <div className="flex-1">
          <h2 className="font-serif-title text-2xl sm:text-3xl text-[#B4483A]">Envie de se dire des choses ? <span className="text-sm font-sans text-[var(--ink-soft)]">(optionnel)</span></h2>
          <p className="text-sm text-[var(--ink-soft)]">Chacun devine pour l'autre, puis on compare. Un moment pour s'écouter, sans procès.</p>
        </div>
        <ChevronDown size={20} className={`text-[var(--ink-soft)] transition-transform ${dire.on ? "rotate-180" : ""}`} />
      </button>
      {dire.on && (
        <div className="px-5 sm:px-7 pb-6 space-y-5 animate-fade-up" data-testid="sc-dire-body">
          <div className="grid md:grid-cols-2 gap-4">
            <Column who={`👦 ${child}`} other={parent} color="#B8781A" bg="#FFF3D6" values={dire.enfant || {}} onChange={(id, v) => setAns("enfant", id, v)} testid="sc-dire-enfant" />
            <Column who={`🧑 ${parent}`} other={child} color="#237572" bg="#E3F4F2" values={dire.parent || {}} onChange={(id, v) => setAns("parent", id, v)} testid="sc-dire-parent" />
          </div>
          <div className="flex justify-center">
            <button onClick={() => setReveal((r) => !r)} data-testid="sc-dire-reveal" className="px-6 py-2.5 rounded-full bg-[#E0705C] text-white text-sm font-bold shadow-sm hover:brightness-105">{reveal ? "Masquer la comparaison" : "Comparer nos réponses"}</button>
          </div>
          {reveal && (
            <div className="space-y-3 animate-fade-up" data-testid="sc-dire-compare">
              {DIRE_QUESTIONS.map((q) => (
                <div key={q.id} className="rounded-2xl border border-[var(--border)] p-4">
                  <div className="text-xs uppercase tracking-wider font-bold text-[var(--ink-soft)] mb-2">{q.id === "trop" ? "Trop entendu cette semaine" : q.id === "pas" ? "Pas assez entendu cette semaine" : q.id === "exag" ? "Exagéré chez moi" : "Pas exagéré chez l'autre"}</div>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-[#FFF3D6] px-3 py-2"><span className="font-bold text-[#B8781A]">{child} :</span> {(dire.enfant || {})[q.id] || <em className="text-[var(--ink-soft)]">—</em>}</div>
                    <div className="rounded-xl bg-[#E3F4F2] px-3 py-2"><span className="font-bold text-[#237572]">{parent} :</span> {(dire.parent || {})[q.id] || <em className="text-[var(--ink-soft)]">—</em>}</div>
                  </div>
                </div>
              ))}
              <p className="text-xs italic text-[var(--ink-soft)] text-center">On écoute, on remercie, on ne discute pas les ressentis. Ce qui a trop été entendu peut devenir un rappel à faire disparaître ; ce qui n'a pas assez été entendu, un renforcement à ajouter.</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
